import type { CSSProperties } from 'react';
import {
  DARK_MODE_SURFACE,
  TEXT_CONTRAST_MIN,
  ensureContrastOnDark,
  getContrastForeground,
} from '@/lib/ui/colour-utils';

const DEFAULT_PRIMARY = '#6366f1';
const DEFAULT_SECONDARY = '#818cf8';
const DEFAULT_PRIMARY_RGB = '99 102 241';
const DEFAULT_SECONDARY_RGB = '129 140 248';

const hexToRgb = (hexColor: string) => {
  const normalized = hexColor.trim().replace('#', '');
  const isShort = normalized.length === 3;
  const isValid = isShort || normalized.length === 6;
  if (!isValid || !/^[0-9a-fA-F]+$/.test(normalized)) {
    return null;
  }
  const full = isShort
    ? normalized
        .split('')
        .map((char) => `${char}${char}`)
        .join('')
    : normalized;
  const red = parseInt(full.slice(0, 2), 16);
  const green = parseInt(full.slice(2, 4), 16);
  const blue = parseInt(full.slice(4, 6), 16);
  return `${red} ${green} ${blue}`;
};

/**
 * Background and text colour for a solid brand-coloured CTA. In dark mode a
 * brand colour too dark to stand out is lightened (#851). The same colour
 * also paints brand text, selected states, the active step, badges and focus
 * rings, so it is lightened to the text minimum of 4.5:1 rather than 3:1
 * (#930). The text colour is always chosen against the background actually
 * painted.
 */
export const resolveBrandCtaColours = (
  colour: string,
  isDark?: boolean
): { background: string; foreground: string } => {
  const background = isDark
    ? ensureContrastOnDark(colour, DARK_MODE_SURFACE, TEXT_CONTRAST_MIN)
    : colour;
  return { background, foreground: getContrastForeground(background) };
};

export const buildBrandStyle = (
  primary?: string | null,
  secondary?: string | null,
  isDark?: boolean
): CSSProperties => {
  const primaryColor = primary || DEFAULT_PRIMARY;
  const secondaryColor = secondary || primaryColor || DEFAULT_SECONDARY;
  const primaryRgb = hexToRgb(primaryColor) || DEFAULT_PRIMARY_RGB;
  const secondaryRgb = hexToRgb(secondaryColor) || DEFAULT_SECONDARY_RGB;
  const computedForeground = getContrastForeground(primaryColor);
  const cta = resolveBrandCtaColours(primaryColor, isDark);

  return {
    '--org-primary': primaryColor,
    '--org-secondary': secondaryColor,
    '--org-primary-rgb': primaryRgb,
    '--org-secondary-rgb': secondaryRgb,
    '--primary': cta.background,
    '--primary-foreground': cta.foreground,
    '--ring': cta.background,
    '--accent': primaryColor,
    // In dark mode, ghost/outline button backgrounds are always dark
    // (accent/50 or input/50 over dark bg), so use white for legibility
    '--accent-foreground': isDark ? '#ffffff' : computedForeground,
  } as CSSProperties;
};
